import { useEffect, useMemo } from 'react';
import { template } from 'lodash';

import { HoneReportTypes } from 'domain/models';
import { AnalyticsEvents, trackEvent } from 'lib/analytics';
import * as ReportUtils from 'lib/reportUtils';
import { useActiveReportStore } from 'hooks/useActiveReportStore';
import { shallow } from 'zustand/shallow';
import { getHeaderFormattedText } from 'presentation/utils';
import { ReportColumnTypes } from '@hone-automation/common';
import NewTable from '../../../components/NewTable';
import { LocalErrorBoundary } from 'components/ErrorBoundary';

interface Props {
  columnHeaderTitle: string;
  report: HoneReportSummary;
  customHeaders?: string[];
  hasCharts: boolean;
  selectedReportType: HoneReportType;
  loading?: boolean;
}

function ReportDataTable({ columnHeaderTitle, report, customHeaders = [] }: Props): JSX.Element {
  // Don't use shallow comparison - we need to detect when currentReport object changes
  // even if the shape is similar, because dates array order might have changed
  const { currentReport, yearPlotted } = useActiveReportStore(state => ({
    currentReport: state.currentReport,
    status: state.status,
    smoothingEnabled: state.smoothingEnabled,
    yearPlotted: state.yearPlotted,
  }));

  const reportType = currentReport ? currentReport.type : '';
  const reportTimeframe = currentReport ? currentReport.timeframe : '';
  const reportDates = useMemo(() => {
    return currentReport ? currentReport.dates : [];
  }, [currentReport]);
  const reportSections = useMemo(() => {
    return currentReport ? currentReport.sections : [];
  }, [currentReport]);

  const eventParams = useMemo(() => {
    if (currentReport) {
      return {
        type: reportType,
        timeframe: reportTimeframe,
        templateId: currentReport.id,
      };
    } else {
      return {
        type: '',
        timeframe: '',
        templateId: '',
      };
    }
  }, [currentReport, reportType, reportTimeframe]);

  useEffect(() => {
    trackEvent(AnalyticsEvents.ReportViewed, eventParams);
  }, [eventParams]);

  const memo = useMemo(() => {
    if (currentReport) {
      const rows = ReportUtils.collectRows(Array.from(reportSections) as any);

      // Generate headers using the same logic as ReportHoneTable
      const headers = ['Week Ending'];

      // Append formatted headers for each date column
      reportDates.forEach((date: any) => {
        const hasSubheader =
          (date.type && date.type === ReportColumnTypes.Data) || date.type === ReportColumnTypes.Budget;

        const subheaderLabel = hasSubheader && date.type === ReportColumnTypes.Data ? 'ACTUAL' : 'BUDGET';

        // Use the same getHeaderFormattedText function for consistency
        const headerFormatted = getHeaderFormattedText(date, currentReport, yearPlotted, false);

        // Use the same getPrintedHeaderDate function for consistency
        const printedDate = ReportUtils.getPrintedHeaderDate(headerFormatted, null, false);

        // Format header the same way
        const formattedHeader = (printedDate as string).concat(hasSubheader ? ` ${subheaderLabel}` : '');

        // Apply custom header if available
        const customHeader = customHeaders[headers.length];
        if (customHeader) {
          const compiled = template(customHeader);
          headers.push(compiled({ value: formattedHeader }));
        } else {
          headers.push(formattedHeader);
        }
      });

      // allow customizing all report types
      const defaultReportStyles = [
        { currentReport: 'title', width: '20%' },
        ...reportDates.map((_, index) => ({ currentReport: `data.${index}`, width: '200px' })),
      ];
      const cashFlowReportStyles = [
        { currentReport: 'title', width: '60%' },
        ...reportDates.map((_, index) => ({ currentReport: `data.${index}`, width: '200px' })),
      ];
      const columnStyles = {
        [HoneReportTypes.CashFlow]: cashFlowReportStyles,
        [HoneReportTypes.BalanceSheet]: cashFlowReportStyles,
        [HoneReportTypes.PLComparison]: defaultReportStyles,
        [HoneReportTypes.PLDetail]: defaultReportStyles,
        [HoneReportTypes.IncomeStatement]: defaultReportStyles,
        [HoneReportTypes.PrimeCost]: defaultReportStyles,
      };
      const columns = columnStyles[reportType as HoneReportType];
      return { rows, headers, columns };
    } else {
      return {};
    }
  }, [currentReport, report, reportDates, reportSections, reportType, columnHeaderTitle, customHeaders, yearPlotted]);

  const MemoizedNewTable = useMemo(() => <NewTable report={currentReport} />, [currentReport]);

  return <>{MemoizedNewTable}</>;
}

const ReportDataTableWrapped = (props: Props) => (
  <LocalErrorBoundary>
    <ReportDataTable {...props} />
  </LocalErrorBoundary>
);

export default ReportDataTableWrapped;
